/*
  HS Kampagnen-Sprungnavigation
  Gehört zu sections/hs-sale-nav.liquid und assets/hs-sale.css.

  Die Navigation ist auf dem Telefon eine waagerecht scrollbare Leiste. Ohne
  Hilfe sieht man ihr das nicht an: die Scrollleiste ist ausgeblendet, und ein
  am Displayrand abgeschnittenes Wort liest sich wie ein Textfehler, nicht wie
  ein Hinweis auf mehr Inhalt. Dieses Skript übernimmt deshalb zwei Aufgaben.

  1. Randzustand. Solange links oder rechts noch etwas zu holen ist, bekommt
     die Leiste data-overflow-start beziehungsweise data-overflow-end. Das CSS
     blendet daran eine weiche Kante ein, an der man sieht, dass es weitergeht.

  2. Aktiver Abschnitt. Beim Scrollen wird der Link des Abschnitts markiert,
     in dem man gerade steht, und in den sichtbaren Bereich der Leiste
     gefahren. Damit beantwortet die Leiste auf dem Telefon die Frage, die sie
     auf dem Desktop nebenbei beantwortet: wo bin ich, und was kommt noch.

  Ohne Javascript bleibt die Leiste eine gewöhnliche Liste aus Ankerlinks.
  Sie scrollt dann weiterhin mit dem Finger, nur ohne Kanten und Markierung.
*/

class HsSaleNav extends HTMLElement {
  connectedCallback() {
    this.scroller = this.querySelector('.hs-sale-nav__list');
    if (!this.scroller) return;

    this.links = Array.from(this.querySelectorAll('.hs-sale-nav__link'));

    // Nur Links, deren Ziel es auf dieser Seite auch gibt. Ein Sprunglink auf
    // einen entfernten Block soll die Markierung nicht durcheinanderbringen.
    this.targets = this.links
      .map((link) => {
        const hash = (link.getAttribute('href') || '').split('#')[1];
        if (!hash) return null;
        const target = document.getElementById(decodeURIComponent(hash));
        return target ? { link, target } : null;
      })
      .filter(Boolean);

    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    this.onScroll = this.schedule.bind(this, this.update.bind(this));
    this.onEdges = this.schedule.bind(this, this.updateEdges.bind(this));

    this.scroller.addEventListener('scroll', this.onEdges, { passive: true });
    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll, { passive: true });

    // Die Schriften laden mit font-display: swap. Tauscht der Browser die
    // Ersatzschrift gegen Roboto, ändern die Beschriftungen ihre Breite, und
    // aus einer passenden Leiste wird eine scrollende. Dafür gibt es kein
    // Scroll- und kein Resize-Ereignis, die weiche Kante bliebe sonst aus.
    if ('ResizeObserver' in window) {
      this.observer = new ResizeObserver(this.onEdges);
      this.observer.observe(this.scroller);
      for (const item of this.scroller.children) this.observer.observe(item);
    }

    this.update();
  }

  disconnectedCallback() {
    this.scroller?.removeEventListener('scroll', this.onEdges);
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onScroll);
    this.observer?.disconnect();
    if (this.frame) cancelAnimationFrame(this.frame);
  }

  // Scroll- und Resize-Ereignisse kommen in Bündeln. Gerechnet wird einmal
  // je Bild, sonst liest jede Messung das Layout neu ein.
  schedule(work) {
    if (this.frame) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = null;
      work();
    });
  }

  update() {
    this.updateEdges();
    this.updateActive();
  }

  updateEdges() {
    const { scrollLeft, scrollWidth, clientWidth } = this.scroller;
    // Ein Pixel Toleranz, weil Browser bei fraktionalen Breiten rundet und
    // scrollLeft am Ende sonst nie genau scrollWidth - clientWidth erreicht.
    this.scroller.toggleAttribute('data-overflow-start', scrollLeft > 1);
    this.scroller.toggleAttribute('data-overflow-end', scrollLeft + clientWidth < scrollWidth - 1);
  }

  updateActive() {
    if (this.targets.length === 0) return;

    // Die Leiste klebt unter dem Header und verdeckt selbst einen Streifen.
    // Gemessen wird deshalb an ihrer Unterkante, nicht am Fensterrand.
    const line = this.getBoundingClientRect().bottom + 1;

    let current = null;
    for (const entry of this.targets) {
      const box = entry.target.getBoundingClientRect();
      if (box.top <= line && box.bottom > line) {
        current = entry;
        break;
      }
      if (box.top <= line) current = entry;
    }

    // Am Seitenende gewinnt der letzte Abschnitt. Sonst bliebe ein kurzer
    // letzter Block unmarkiert, weil man nicht weit genug scrollen kann.
    if (window.innerHeight + window.scrollY >= document.body.scrollHeight - 2) {
      current = this.targets[this.targets.length - 1];
    }

    if (current === this.current) return;
    this.current = current;

    for (const entry of this.targets) {
      const active = entry === current;
      entry.link.classList.toggle('is-active', active);
      if (active) {
        entry.link.setAttribute('aria-current', 'true');
      } else {
        entry.link.removeAttribute('aria-current');
      }
    }

    if (current) this.reveal(current.link);
  }

  // Den aktiven Link in die Leiste fahren, ohne die Seite zu bewegen.
  // scrollIntoView kann die Seite mitscrollen, deshalb wird der Versatz
  // selbst gerechnet.
  reveal(link) {
    const scroller = this.scroller;
    if (scroller.scrollWidth <= scroller.clientWidth) return;

    // Nicht offsetLeft: dessen Bezug ist der nächste positionierte Vorfahr,
    // und das ist hier der klebende Section-Wrapper, nicht die Leiste.
    const item = link.parentElement || link;
    const box = item.getBoundingClientRect();
    const frame = scroller.getBoundingClientRect();
    const within = box.left - frame.left + scroller.scrollLeft;
    const left = within - (scroller.clientWidth - box.width) / 2;
    const max = scroller.scrollWidth - scroller.clientWidth;
    const target = Math.max(0, Math.min(left, max));

    if (Math.abs(target - scroller.scrollLeft) < 2) return;

    scroller.scrollTo({
      left: target,
      behavior: this.reducedMotion.matches ? 'auto' : 'smooth',
    });
  }
}

if (!customElements.get('hs-sale-nav')) {
  customElements.define('hs-sale-nav', HsSaleNav);
}
